import {
  collection,
  getDocs,
  query,
  where,
  limit,
  startAfter,
  orderBy,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "@/services/firebase/firebase";
import type { LawyerIndex } from "@/types/firebase/search";
import { getWorkAreaSearchLabels } from "@/constants/workAreas";

// Re-export types for convenience
export type { LawyerIndex } from "@/types/firebase/search";

const col = collection(db, "searchIndex", "lawyers", "items");

// Add AbortController support for search cancellation
let currentSearchController: AbortController | null = null;

export const normalizeLawyerNameSearchTerm = (name?: string): string =>
  (name || "")
    .normalize("NFKC")
    .replace(/[\u2018\u2019\u201B\u2032]/g, "'")
    .replace(/[\u2010-\u2015]/g, "-")
    .replace(/\s+/g, " ")
    .toLowerCase()
    .trim();

const foldLawyerNameForSearch = (name?: string): string =>
  normalizeLawyerNameSearchTerm(name)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const isVerifiedLawyerNameSearchTerm = (name?: string): boolean => {
  const normalizedName = normalizeLawyerNameSearchTerm(name);
  if (!normalizedName) return true;

  return (
    normalizedName.length >= 2 &&
    /^[\p{L}\p{M}.'’`´\-()\s]+$/u.test(normalizedName)
  );
};

const filterLawyersByName = (
  items: LawyerIndex[],
  lawyerName?: string,
): LawyerIndex[] => {
  const normalizedName = normalizeLawyerNameSearchTerm(lawyerName);
  if (!normalizedName || !isVerifiedLawyerNameSearchTerm(normalizedName)) {
    return items;
  }

  const foldedName = foldLawyerNameForSearch(normalizedName);

  return items.filter((lawyer) => {
    const indexedName = normalizeLawyerNameSearchTerm(lawyer.nameNormalized);
    const displayName = normalizeLawyerNameSearchTerm(lawyer.name);
    const foldedIndexedName = foldLawyerNameForSearch(lawyer.nameNormalized);
    const foldedDisplayName = foldLawyerNameForSearch(lawyer.name);

    return (
      indexedName.includes(normalizedName) ||
      displayName.includes(normalizedName) ||
      foldedIndexedName.includes(foldedName) ||
      foldedDisplayName.includes(foldedName)
    );
  });
};

export const listLawyers = async (): Promise<LawyerIndex[]> => {
  const snap = await getDocs(query(col, where("profileComplete", "==", true)));
  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<LawyerIndex, "id">),
  }));
};

export const searchLawyers = async (
  specialties: string[] = [],
  location?: string,
  pageSize = 50,
  cursor?: any,
): Promise<{ items: LawyerIndex[]; nextCursor: any }> => {
  let q: any = query(
    col,
    where("profileComplete", "==", true),
    orderBy("searchPriority", "desc"), orderBy("createdAt", "asc"),
    limit(pageSize),
  );

  if (specialties.length) {
    q = query(
      col,
      where("profileComplete", "==", true),
      where("specialties", "array-contains-any", specialties),
      orderBy("searchPriority", "desc"), orderBy("createdAt", "asc"),
      limit(pageSize),
    );
  }

  if (cursor) q = query(q, startAfter(cursor));

  const snap = await getDocs(q);
  let items = snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<LawyerIndex, "id">),
  }));

  // Enhanced location filtering with multiple location components
  if (location) {
    const locationLower = location.toLowerCase();
    items = items.filter((lawyer) => {
      const mainLocation = (lawyer.location || "").toLowerCase();
      const city = (lawyer.city || "").toLowerCase();
      const state = (lawyer.state || "").toLowerCase();
      const country = (lawyer.country || "").toLowerCase();

      return (
        mainLocation.includes(locationLower) ||
        city.includes(locationLower) ||
        state.includes(locationLower) ||
        country.includes(locationLower)
      );
    });
  }

  const nextCursor =
    snap.docs.length === pageSize ? snap.docs[snap.docs.length - 1] : null;
  return { items, nextCursor };
};

// Optimized search function with better query structure and reduced client-side filtering
export const searchLawyersByLocation = async (
  country?: string,
  state?: string,
  city?: string,
  specialties: string[] = [],
  subCategory?: string,
  lawyerName?: string,
  pageSize = 50,
  cursor?: any,
): Promise<{ items: LawyerIndex[]; nextCursor: any }> => {
  const normalizedLawyerName = normalizeLawyerNameSearchTerm(lawyerName);
  const hasLawyerNameFilter = Boolean(normalizedLawyerName);

  // Cancel previous search if still running
  if (currentSearchController) {
    currentSearchController.abort();
  }
  currentSearchController = new AbortController();

  try {
    // Build optimized query with minimal conditions
    let q: any = query(col, where("profileComplete", "==", true));

    // Add location filters in order of specificity (most specific first for better indexing)
    if (city) {
      q = query(q, where("city", "==", city), orderBy("searchPriority", "desc"), orderBy("createdAt", "asc"));
    } else if (state) {
      q = query(q, where("state", "==", state), orderBy("searchPriority", "desc"), orderBy("createdAt", "asc"));
    } else if (country) {
      q = query(q, where("country", "==", country), orderBy("searchPriority", "desc"), orderBy("createdAt", "asc"));
    } else {
      q = query(q, orderBy("searchPriority", "desc"), orderBy("createdAt", "asc"));
    }

    if (!hasLawyerNameFilter) {
      q = query(q, limit(pageSize));
    }

    // Add specialty filter if provided
    if (specialties.length) {
      // For composite queries, we need to be careful about indexing
      // We'll do specialty filtering client-side to avoid complex composite indexes
    }

    if (cursor && !hasLawyerNameFilter) q = query(q, startAfter(cursor));

    const snap = await getDocs(q);
    let items = snap.docs.map((d) => ({
      id: d.id,
      ...(d.data() as Omit<LawyerIndex, "id">),
    }));

    // Filter by specialties client-side if needed
    if (specialties.length) {
      items = items.filter(
        (lawyer) =>
          lawyer.specialties &&
          lawyer.specialties.some((s) => specialties.includes(s)),
      );
    }

    // Pre-filter for lawyers with experience data and appointment types
    items = items.filter((lawyer) => {
      if (
        !lawyer.legal_expertise ||
        typeof lawyer.legal_expertise !== "object"
      ) {
        return false;
      }

      // Note: Appointment type filter disabled for now since no lawyers have added them yet
      // Will be enabled once lawyers start configuring their appointment types
      // const hasAppointmentType =
      //   lawyer.offersVideoCall || lawyer.offersInPerson || lawyer.offersPhone;
      // if (!hasAppointmentType) {
      //   return false;
      // }

      // If searching for specific work areas, check if they have pricing/experience for those areas
      if (specialties.length) {
        return specialties.some((specialty) => {
          const expertise = lawyer.legal_expertise?.[specialty];
          if (!expertise) return false;

          const hasLegacyPrice =
            typeof expertise.price === "number" && expertise.price > 0;
          const hasNewPricing =
            expertise.prices &&
            ((typeof expertise.prices["30"] === "number" &&
              expertise.prices["30"] > 0) ||
              (typeof expertise.prices["60"] === "number" &&
                expertise.prices["60"] > 0));

          return hasLegacyPrice || hasNewPricing;
        });
      }

      // If no specific specialties, check if lawyer has at least one complete area
      return Object.values(lawyer.legal_expertise).some((expertise: any) => {
        if (!expertise) return false;

        const hasLegacyPrice =
          typeof expertise.price === "number" && expertise.price > 0;
        const hasNewPricing =
          expertise.prices &&
          ((typeof expertise.prices["30"] === "number" &&
            expertise.prices["30"] > 0) ||
            (typeof expertise.prices["60"] === "number" &&
              expertise.prices["60"] > 0));

        return hasLegacyPrice || hasNewPricing;
      });
    });

    // Filter by subcategory if specified
    if (subCategory) {
      items = items.filter((lawyer) => {
        if (
          !lawyer.legal_expertise ||
          typeof lawyer.legal_expertise !== "object"
        ) {
          return false;
        }

        return Object.values(lawyer.legal_expertise).some((expertise) => {
          if (!expertise || !expertise.subcategories) {
            return false;
          }
          return expertise.subcategories.includes(subCategory);
        });
      });
    }

    items = filterLawyersByName(items, normalizedLawyerName);

    const nextCursor =
      !hasLawyerNameFilter && snap.docs.length === pageSize
        ? snap.docs[snap.docs.length - 1]
        : null;
    return { items, nextCursor };
  } catch (error: any) {
    if (error.name === "AbortError") {
      throw new Error("Search cancelled");
    }
    throw error;
  }
};

// New function specifically for subcategory searches
export const searchLawyersBySubCategory = async (
  subCategory: string,
  workArea?: string,
  country?: string,
  state?: string,
  city?: string,
  pageSize = 50,
  cursor?: any,
): Promise<{ items: LawyerIndex[]; nextCursor: any }> => {
  let q: any = query(
    col,
    where("profileComplete", "==", true),
    orderBy("searchPriority", "desc"), orderBy("createdAt", "asc"),
    limit(pageSize),
  );

  // Build query conditions
  const conditions = [where("profileComplete", "==", true)];
  if (workArea) {
    const workAreaLabels = getWorkAreaSearchLabels(workArea);
    conditions.push(
      workAreaLabels.length > 1
        ? where("specialties", "array-contains-any", workAreaLabels)
        : where("specialties", "array-contains", workArea),
    );
  }
  if (country) {
    conditions.push(where("country", "==", country));
  }
  if (state) {
    conditions.push(where("state", "==", state));
  }
  if (city) {
    conditions.push(where("city", "==", city));
  }

  // Apply conditions if any
  if (conditions.length > 1) {
    q = query(col, ...conditions, orderBy("searchPriority", "desc"), orderBy("createdAt", "asc"), limit(pageSize));
  }

  if (cursor) q = query(q, startAfter(cursor));

  const snap = await getDocs(q);
  let items = snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<LawyerIndex, "id">),
  }));

  // Filter by subcategory
  items = items.filter((lawyer) => {
    if (!lawyer.legal_expertise || typeof lawyer.legal_expertise !== "object") {
      return false;
    }

    // Check if any of the lawyer's specialties have the specified subcategory
    return Object.values(lawyer.legal_expertise).some((expertise) => {
      if (!expertise || !expertise.subcategories) {
        return false;
      }
      return expertise.subcategories.includes(subCategory);
    });
  });

  const nextCursor =
    snap.docs.length === pageSize ? snap.docs[snap.docs.length - 1] : null;
  return { items, nextCursor };
};

// Function to get lawyer profile for detailed view
export const getLawyerProfile = async (
  lawyerId: string,
): Promise<LawyerIndex | null> => {
  try {
    const docRef = collection(db, "searchIndex", "lawyers", "items");
    const snap = await getDocs(
      query(docRef, where("__name__", "==", lawyerId)),
    );

    if (snap.empty) return null;

    const doc = snap.docs[0];
    return { id: doc.id, ...(doc.data() as Omit<LawyerIndex, "id">) };
  } catch (error) {
    console.error("Error fetching lawyer profile:", error);
    return null;
  }
};

// Function to get complete lawyer profile data from profiles collection
export const getCompleteLawyerProfile = async (
  lawyerId: string,
): Promise<any | null> => {
  try {
    const profileDoc = doc(db, "profiles", lawyerId);
    const profileSnap = await getDoc(profileDoc);

    if (!profileSnap.exists()) return null;

    return { id: profileSnap.id, ...profileSnap.data() };
  } catch (error) {
    console.error("Error fetching complete lawyer profile:", error);
    return null;
  }
};

export const getLawyerBySlug = async (
  slug: string,
): Promise<LawyerIndex | null> => {
  try {
    const qSnap = await getDocs(
      query(col, where("slug", "==", slug), limit(1)),
    );
    if (qSnap.empty) return null;
    const docSnap = qSnap.docs[0];
    return { id: docSnap.id, ...(docSnap.data() as Omit<LawyerIndex, "id">) };
  } catch (err) {
    console.error("Error fetching lawyer by slug", err);
    return null;
  }
};

// Cleanup function
export const cancelCurrentSearch = () => {
  if (currentSearchController) {
    currentSearchController.abort();
    currentSearchController = null;
  }
};
